// InboxScreen.tsx — Unified inbox of customer messages across channels.
// Tabs: Unread / All / Email / @Mentions. Refresh + Mark all read.

import React, { useState, useMemo, useEffect } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform, ActivityIndicator,
} from 'react-native';
import type { Ticket } from '../schema/types';
import { Colors, FontWeight, Radii, Shadow } from '../theme/tokens';
import { useResponsive } from '../theme/responsive';
import { useApp, navigate } from '../store/AppStore';
import { useAuth } from '../store/AuthContext';
import { buildInboxItems } from '../store/inbox';
import { ScreenShell } from '../components/WebShell';
import { Icon } from '../components/Icon';
import { Avatar } from '../components/Avatar';
import PrimaryButton from '../components/Button_Component/Primary_Button';
import CommonSearch from '../components/Common_Search/Common_Search_Component';
import { Chip } from '../components/Chip';
import ConfirmDialog from '../components/ConfirmDialog';
import SuccessDialog from '../components/SuccessDialog';

type InboxTab = 'unread' | 'all' | 'email' | 'mentions';
const INBOX_TABS: InboxTab[] = ['unread', 'all', 'email', 'mentions'];

// ─── Types ────────────────────────────────────────────────────────────────────
interface InboxItem {
  id: string;
  kind: 'message' | 'mention';
  ticketId: string;
  ticketSubject: string;
  channel: string;
  sender: { name: string; email: string; initials: string; color: string };
  snippet: string;
  time: string;
  timeISO?: string | null;
  viaEmail?: boolean;
  unread: boolean;
  priority: string;
  watcherBounce?: boolean;
  commentCount: number;
  assigneeName?: string | null;
  assigneeInitials?: string | null;
  assigneeColor?: string | null;
  assignedToMe?: boolean;
  status?: string;
  category?: string;
  coreIssue?: string;
  createdAtLabel?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function channelIconName(channel: string): 'Mail' | 'Server' | 'Globe' | 'AtSign' {
  const map: Record<string, 'Mail' | 'Server' | 'Globe' | 'AtSign'> = {
    email: 'Mail', api: 'Server', web: 'Globe', agent: 'AtSign',
  };
  return map[channel] ?? 'Globe';
}

function priorityTone(p: string): 'danger' | 'warn' | 'default' {
  if (p === 'high' || p === 'urgent') return 'danger';
  if (p === 'medium') return 'warn';
  return 'default';
}

// Status → dot/label color for the high-level meta footer.
function statusTone(s?: string): string {
  switch (s) {
    case 'resolved': return Colors.success;
    case 'closed':   return Colors.ink4;
    case 'pending':  return Colors.warn;
    default:         return Colors.accent; // open
  }
}

// ─── InboxScreen ──────────────────────────────────────────────────────────────
export function InboxScreen() {
  const { tickets, readInboxIds, markInboxRead, markAllInboxRead, refreshInbox, toast, route, searchTickets, deleteTicket } = useApp();
  const { user } = useAuth();
  const { isMobile, pagePadX } = useResponsive();

  // Initialise tab + search query from the URL (#/inbox?tab=email&q=refund) so
  // the view is shareable and survives a refresh.
  const initialTab: InboxTab = INBOX_TABS.includes(route?.params?.tab as InboxTab)
    ? (route!.params!.tab as InboxTab)
    : 'unread';
  const [active, setActive] = useState<InboxTab>(initialTab);
  const [channelFilter, setChannelFilter] = useState('all');
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [query, setQuery] = useState<string>(route?.params?.q ?? '');

  // Delete flow: confirm → (API) → success. `pendingDelete` holds the inbox item
  // awaiting confirmation; `deleting` guards the in-flight request; `deleted`
  // carries the just-removed item so the success dialog can name it.
  const [pendingDelete, setPendingDelete] = useState<InboxItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState<InboxItem | null>(null);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const ok = await deleteTicket(pendingDelete.ticketId);
    setDeleting(false);
    if (ok) {
      // When a search is active the list renders from local searchResults, not
      // the store's tickets — prune it too so the row disappears immediately.
      setSearchResults(prev => prev ? prev.filter(t => t.id !== pendingDelete.ticketId) : prev);
      setDeleted(pendingDelete);   // open success dialog
      setPendingDelete(null);      // close confirm dialog
    } else {
      setPendingDelete(null);      // failure already toasted by the store
    }
  };

  // Server-side search results. `null` = no active search (use the loaded
  // inbox); an array = results from GET /v1/tickets?q=… .
  const [searchResults, setSearchResults] = useState<Ticket[] | null>(null);
  const [searching, setSearching] = useState(false);
  const searchActive = query.trim().length > 0;
  // A pure ticket-number reference ("3", "#3", "TKT-2"). When the user searches
  // by number we show the matched ticket regardless of the active tab.
  const isNumberQuery = /^(?:tkt[-\s]?)?#?\d+$/i.test(query.trim());

  // Debounced server search. Hits the API full-text endpoint as the user types
  // and drives the inbox from those rows. Empty query → clear back to the inbox.
  useEffect(() => {
    const q = query.trim();
    if (!q) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const results = await searchTickets(q);
        if (!cancelled) setSearchResults(results);
      } catch {
        if (!cancelled) { setSearchResults([]); toast('Search failed. Try again.', 'danger'); }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(handle); };
    // searchTickets is stable enough; re-running only on query keeps it to one
    // request per keystroke-pause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Mirror the active tab + query back into the URL hash (web only). We use
  // replaceState so live typing doesn't spam the browser history.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const parts: string[] = [];
    if (active !== 'unread') parts.push('tab=' + encodeURIComponent(active));
    const q = query.trim();
    if (q) parts.push('q=' + encodeURIComponent(q));
    const target = '/inbox' + (parts.length ? '?' + parts.join('&') : '');
    const current = window.location.pathname + window.location.search;
    if (current !== target) {
      window.history.replaceState(null, '', target);
    }
  }, [active, query]);
  // Real persisted read-state from the store.
  const readIds = useMemo(() => new Set<string>(readInboxIds ?? []), [readInboxIds]);

  // When a search is active the inbox is built from the API search results;
  // otherwise from the loaded inbox tickets.
  const sourceTickets: Ticket[] = searchActive ? (searchResults ?? []) : tickets;

  // Ticket-centric inbox feed — shared with the sidebar/bell counts so they
  // never disagree. Each ticket is one conversation; the snippet upgrades from
  // the subject to the latest customer message once a thread has been opened.
  const items: InboxItem[] = useMemo(() => buildInboxItems(sourceTickets, readIds, user?.id), [sourceTickets, readIds, user?.id]);

  const mentions: InboxItem[] = useMemo(() => {
    const out: InboxItem[] = [];
    // Generic @handle mention detection — any "@token" in an internal comment body.
    sourceTickets.forEach((t) => {
      t.comments.forEach((c) => {
        const body = typeof c.body === 'string' ? c.body : '';
        if (c.type === 'internal' && /@([a-zA-Z0-9._-]+)/g.test(body)) {
          const id = t.id + ':mention:' + c.id;
          const name = c.author ?? 'Internal note';
          const initials = name
            .split(/\s+/)
            .map((w: string) => w.charAt(0))
            .join('')
            .slice(0, 2)
            .toUpperCase() || '@';
          out.push({
            id, kind: 'mention', ticketId: t.id,
            ticketSubject: t.subject, channel: t.channel,
            sender: {
              name, initials,
              color: 'linear-gradient(135deg,#10b981,#0ea5e9)',
              email: '',
            },
            snippet: body,
            time: c.time,
            unread: !readIds.has(id),
            priority: t.priority,
            commentCount: t.comments.length,
          });
        }
      });
    });
    return out;
  }, [sourceTickets, readIds]);

  // Tabs + channel scoping. When a search is active, `items`/`mentions` are
  // already the API-matched results, so the tabs just scope within them.
  const tabFiltered: InboxItem[] = useMemo(() => {
    // Ticket-number search: show every matched ticket, ignoring tab/channel
    // scoping (so "TKT-2" surfaces no matter which tab you're on).
    if (searchActive && isNumberQuery) return items;

    let list = active === 'mentions' ? mentions : items;
    if (active === 'unread') list = items.filter(i => i.unread);
    if (active === 'email')  list = items.filter(i => i.channel === 'email');
    if (active !== 'mentions' && channelFilter !== 'all') list = list.filter(i => i.channel === channelFilter);
    return list;
  }, [active, channelFilter, items, mentions, searchActive, isNumberQuery]);

  const unreadCount  = items.filter(i => i.unread).length;
  const emailCount   = items.filter(i => i.channel === 'email').length;
  const mentionCount = mentions.filter(m => m.unread).length;

  const handleMarkItem = (id: string, ticketId: string) => {
    markInboxRead([id]);
    navigate('/tickets/' + ticketId);
  };

  const handleMarkAll = () => {
    const ids = tabFiltered.filter(i => i.unread).map(i => i.id);
    if (ids.length === 0) { toast('Nothing to mark.', 'info'); return; }
    markAllInboxRead(ids);
  };

  const TABS: Array<{ key: 'unread' | 'all' | 'email' | 'mentions'; label: string; count: number }> = [
    { key: 'unread',   label: 'Unread',    count: unreadCount },
    { key: 'all',      label: 'All',       count: items.length },
    { key: 'email',    label: 'Email',     count: emailCount },
    { key: 'mentions', label: '@Mentions', count: mentionCount },
  ];

  const CHANNEL_OPTS = ['all', 'email', 'web', 'api'];

  // Premium empty-state content — a positive, on-brand message per tab so an
  // empty inbox reads as "you're on top of things", never as "broken".
  const emptyState: { icon: 'CheckCircle' | 'Inbox' | 'Mail' | 'AtSign'; title: string; sub: string } =
    query.trim()
      ? { icon: 'Inbox', title: 'No matches found', sub: `Nothing matched “${query.trim()}”. Try a different name, subject, or ticket number.` }
    : active === 'unread'
      ? { icon: 'CheckCircle', title: 'Inbox zero — beautifully done!', sub: "You've read everything. Enjoy the calm — new arrivals will land here the moment they come in." }
    : active === 'email'
      ? { icon: 'Mail', title: 'No emails waiting', sub: 'Every customer email has been handled. Fresh ones will appear here automatically.' }
    : active === 'mentions'
      ? { icon: 'AtSign', title: "You're all caught up on mentions", sub: 'Nobody needs your attention right now. When a teammate @mentions you, it shows up here.' }
      : { icon: 'Inbox', title: 'Your inbox is all clear', sub: "No messages right now. When customers reach out, they'll arrive here first — you're ready." };

  return (
    <ScreenShell crumbs={[{ label: 'Inbox' }]}>
      <View style={styles.root}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <View style={[styles.header, { paddingHorizontal: pagePadX }, isMobile && { flexWrap: 'wrap' }]}>
          <View style={styles.headerLeft}>
            <Text style={styles.title}>Inbox</Text>
            <Text style={styles.subtitle}>
              {searchActive
                ? (searching
                    ? `Searching for "${query.trim()}"…`
                    : `${tabFiltered.length} result${tabFiltered.length === 1 ? '' : 's'} for "${query.trim()}"`)
                : `${tabFiltered.length} of ${items.length + mentions.length} messages · across email, web, API, and @mentions`}
            </Text>
          </View>
          <View style={styles.headerRight}>
            <PrimaryButton
              title="Refresh"
              size="sm"
              variant="default"
              leftIcon={<Icon name="Refresh" size={13} color={Colors.ink2} />}
              onPress={() => refreshInbox()}
            />
            <PrimaryButton
              title="Mark all read"
              size="sm"
              variant="default"
              leftIcon={<Icon name="Check" size={13} color={Colors.ink2} />}
              onPress={handleMarkAll}
              disabled={tabFiltered.filter(i => i.unread).length === 0}
            />
          </View>
        </View>

        {/* ── Search (tickets · email · @mentions) ────────────────────────── */}
        <View style={[styles.searchRow, { paddingHorizontal: pagePadX }]}>
          <View style={styles.searchInner}>
            <CommonSearch
              value={query}
              onChange={setQuery}
              onSubmit={() => {}}
              placeholder="Search by ticket , subject, email, @mentions…"
            />
          </View>
        </View>

        {/* ── Tabs + Channel Filter ───────────────────────────────────────── */}
        <View style={[styles.tabBar, { paddingHorizontal: pagePadX }]}>
          {TABS.map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tabBtn, active === tab.key && styles.tabBtnActive]}
              onPress={() => setActive(tab.key)}
              activeOpacity={0.8}
            >
              <Text style={[styles.tabBtnText, active === tab.key && styles.tabBtnTextActive]}>
                {tab.label}
              </Text>
              <Text style={[styles.tabCount, active === tab.key && styles.tabCountActive]}>
                {tab.count}
              </Text>
            </TouchableOpacity>
          ))}

          <View style={styles.divider} />
          <Text style={styles.channelLabel}>Channel:</Text>

          {CHANNEL_OPTS.map(ch => (
            <TouchableOpacity
              key={ch}
              style={[styles.chanBtn, channelFilter === ch && styles.chanBtnActive]}
              onPress={() => setChannelFilter(ch)}
              activeOpacity={0.8}
            >
              <Text style={[styles.chanBtnText, channelFilter === ch && styles.chanBtnTextActive]}>
                {ch === 'all' ? 'All' : ch.toUpperCase() === 'API' ? 'API' : ch.charAt(0).toUpperCase() + ch.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* ── List ───────────────────────────────────────────────────────── */}
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {searchActive && searching ? (
            <View style={styles.empty}>
              <ActivityIndicator size="small" color={Colors.accent} />
              <Text style={styles.emptySub}>Searching…</Text>
            </View>
          ) : tabFiltered.length === 0 ? (
            Platform.OS === 'web' ? (
              <div className="sk-empty">
                <div className="sk-empty-icon">
                  <Icon name={emptyState.icon} size={32} color={Colors.accent} />
                </div>
                <div className="sk-empty-title">{emptyState.title}</div>
                <div className="sk-empty-sub">{emptyState.sub}</div>
              </div>
            ) : (
              <View style={styles.empty}>
                <Icon name={emptyState.icon} size={40} color={Colors.accent} />
                <Text style={styles.emptyTitle}>{emptyState.title}</Text>
                <Text style={styles.emptySub}>{emptyState.sub}</Text>
              </View>
            )
          ) : (
            <View style={styles.listStack}>
              {tabFiltered.map((item) => (
                <TouchableOpacity
                  key={item.id}
                  style={[
                    styles.listItem,
                    item.unread && styles.listItemUnread,
                    hoveredId === item.id && styles.listItemHover,
                  ]}
                  onPress={() => handleMarkItem(item.id, item.ticketId)}
                  activeOpacity={0.9}
                  {...(Platform.OS === 'web' ? {
                    onMouseEnter: () => setHoveredId(item.id),
                    onMouseLeave: () => setHoveredId(null),
                  } as any : {})}
                >
                  {/* Unread accent rail — a soft vertical bar on the card's
                      leading edge so unread threads read at a glance. */}
                  {item.unread && <View style={styles.unreadRail} />}

                  {/* Unread dot */}
                  <View style={[styles.unreadDot, item.unread && styles.unreadDotActive]} />

                  {/* Avatar */}
                  <Avatar initials={item.sender.initials} color={item.sender.color} size="sm" />

                  {/* Channel / mention icon */}
                  <View style={styles.channelIconWrap}>
                    {item.kind === 'mention'
                      ? <Icon name="AtSign" size={11} color={Colors.ink3} />
                      : <Icon name={channelIconName(item.channel)} size={11} color={Colors.ink3} />
                    }
                  </View>

                  {/* Main content */}
                  <View style={styles.itemBody}>
                    {/* Row 1: sender name + email + tags */}
                    <View style={styles.itemRow}>
                      <Text style={[styles.senderName, item.unread && styles.senderNameUnread]} numberOfLines={1}>
                        {item.sender.name}
                      </Text>
                      <Text style={styles.senderEmail} numberOfLines={1}>· {item.sender.email}</Text>
                      {item.kind === 'mention' && (
                        <Text style={styles.mentionTag}>· @mention</Text>
                      )}
                      {item.watcherBounce && (
                        <View style={styles.bounceWrap}>
                          <Icon name="Alert" size={11} color={Colors.warn} stroke={2.2} />
                          <Text style={styles.bounceText}>bounce</Text>
                        </View>
                      )}
                    </View>

                    {/* Row 2: ticket ID + subject */}
                    <View style={[styles.itemRow, styles.itemRowGap]}>
                      <View style={styles.ticketIdBadge}>
                        <Text style={styles.ticketIdHash}>#</Text>
                        <Text style={styles.ticketIdText}>{item.ticketId.replace(/^TKT-/, '')}</Text>
                      </View>
                      <Text
                        style={[styles.ticketSubject, item.unread && styles.ticketSubjectUnread]}
                        numberOfLines={1}
                      >
                        {item.ticketSubject}
                      </Text>
                    </View>

                    {/* Row 3: core issue / latest message */}
                    <Text style={styles.snippet} numberOfLines={2}>
                      {item.coreIssue || item.snippet}
                    </Text>

                    {/* Row 4: high-level facts — status · created · category · assignee */}
                    <View style={styles.metaFooter}>
                      <View style={styles.metaPill}>
                        <View style={[styles.statusDot, { backgroundColor: statusTone(item.status) }]} />
                        <Text style={[styles.metaPillText, { color: statusTone(item.status), fontWeight: FontWeight.semibold }]}>
                          {(item.status ?? 'open').replace(/^\w/, c => c.toUpperCase())}
                        </Text>
                      </View>

                      <View style={styles.metaDivider} />

                      <View style={styles.metaPill}>
                        <Icon name="Clock" size={11} color={Colors.ink4} />
                        <Text style={styles.metaPillText}>{item.createdAtLabel ?? item.time}</Text>
                      </View>

                      {!!item.category && (
                        <View style={styles.metaPill}>
                          <Icon name="Tag" size={11} color={Colors.ink4} />
                          <Text style={styles.metaPillText} numberOfLines={1}>{item.category}</Text>
                        </View>
                      )}

                      <View style={styles.metaPill}>
                        <Icon name="User" size={11} color={item.assigneeName ? Colors.accent : Colors.ink4} />
                        <Text
                          style={[styles.metaPillText, item.assigneeName && styles.metaPillTextStrong]}
                          numberOfLines={1}
                        >
                          {item.assigneeName ?? 'Unassigned'}
                        </Text>
                      </View>

                      {/* "Assigned to you" — a distinct accent badge whenever the
                          ticket is owned by the viewing user. */}
                      {item.assignedToMe && (
                        <View style={styles.assignedPill}>
                          <Icon name="UserCheck" size={10} color={Colors.accent} stroke={2.4} />
                          <Text style={styles.assignedPillText}>Assigned to you</Text>
                        </View>
                      )}
                    </View>
                  </View>

                  {/* Right meta */}
                  <View style={styles.itemMeta}>
                    <Text
                      style={styles.metaTime}
                      {...(Platform.OS === 'web' && item.timeISO
                        ? { title: new Date(item.timeISO).toLocaleString() } as any
                        : {})}
                    >
                      {item.time}
                    </Text>
                    <Chip
                      label={item.priority}
                      tone={priorityTone(item.priority)}
                      dot={item.priority === 'high' || item.priority === 'urgent'}
                      size="sm"
                    />
                    <Text style={styles.msgCount}>{item.commentCount} msg</Text>
                  </View>

                  {/* Delete action — opens the confirm dialog; never triggers
                      the row's navigation. */}
                  <TouchableOpacity
                    style={[styles.deleteBtn, hoveredId === item.id && styles.deleteBtnVisible]}
                    onPress={(e?: any) => { e?.stopPropagation?.(); setPendingDelete(item); }}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    activeOpacity={0.7}
                    accessibilityLabel={`Delete ticket ${item.ticketId}`}
                  >
                    <Icon name="Trash" size={15} color={hoveredId === item.id ? Colors.danger : Colors.ink4} />
                  </TouchableOpacity>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </ScrollView>
      </View>

      {/* Delete: confirm → success */}
      <ConfirmDialog
        visible={pendingDelete !== null}
        title="Delete ticket?"
        message={
          pendingDelete
            ? `Ticket #${pendingDelete.ticketId.replace(/^TKT-/, '')} "${pendingDelete.ticketSubject}" will be removed from your inbox. This can be restored by an admin.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setPendingDelete(null); }}
      />
      <SuccessDialog
        visible={deleted !== null}
        title="Ticket deleted"
        message={
          deleted
            ? `Ticket #${deleted.ticketId.replace(/^TKT-/, '')} has been removed from your inbox.`
            : undefined
        }
        primaryAction={{ label: 'Done', onPress: () => setDeleted(null) }}
        onClose={() => setDeleted(null)}
      />
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, flexDirection: 'column' },

  // Header
  header: {
    paddingHorizontal: 28, paddingTop: 18, paddingBottom: 14,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card,
    flexDirection: 'row', alignItems: 'flex-end', gap: 16,
  },
  headerLeft: { flex: 1 },
  headerRight: { flexDirection: 'row', gap: 8 },
  title: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  subtitle: { fontSize: 12.5, color: Colors.ink3, marginTop: 3 },

  // Search
  searchRow: {
    paddingVertical: 12,
    backgroundColor: Colors.card,
  },
  searchInner: { width: '100%', maxWidth: 360 },

  // Tabs
  tabBar: {
    paddingHorizontal: 28, paddingVertical: 10,
    backgroundColor: Colors.card,
    flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap',
  },
  tabBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: Radii.r2, backgroundColor: Colors.paper2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  tabBtnActive: { backgroundColor: Colors.accent },
  tabBtnText: { fontSize: 13, color: Colors.ink2, fontWeight: FontWeight.medium },
  tabBtnTextActive: { color: Colors.white, fontWeight: FontWeight.semibold },
  tabCount: { fontSize: 10.5, color: Colors.ink4 },
  tabCountActive: { color: 'rgba(255,255,255,0.7)' },

  divider: { width: 1, height: 18, backgroundColor: Colors.line, marginHorizontal: 8 },
  channelLabel: { fontSize: 12, color: Colors.ink3 },

  chanBtn: {
    paddingHorizontal: 8, paddingVertical: 4,
    borderRadius: Radii.r2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  chanBtnActive: { backgroundColor: Colors.paper2 },
  chanBtnText: { fontSize: 12, color: Colors.ink3 },
  chanBtnTextActive: { color: Colors.ink, fontWeight: FontWeight.medium },

  // Scroll / list
  scroll: { flex: 1 },
  scrollContent: { padding: 28, paddingTop: 8 },

  // Gapped column of standalone cards — each ticket is its own surface with
  // breathing room between them. Capped + left-aligned on wide screens.
  listStack: {
    marginTop: 8, gap: 12,
    width: '100%', maxWidth: 860, alignSelf: 'flex-start',
  },
  // Each ticket is an elevated card: generously rounded, hairline-bordered,
  // softly shadowed. It lifts on hover for a tactile, premium feel.
  listItem: {
    position: 'relative',
    flexDirection: 'row', alignItems: 'flex-start', gap: 14,
    paddingHorizontal: 22, paddingVertical: 20,
    backgroundColor: Colors.card,
    borderWidth: 1, borderColor: Colors.line,
    borderRadius: Radii.r5,
    overflow: 'hidden',
    ...(Shadow.base as object),
    ...(Platform.OS === 'web'
      ? { transitionProperty: 'box-shadow, transform, border-color', transitionDuration: '180ms', transitionTimingFunction: 'cubic-bezier(.2,.7,.3,1)' } as any
      : {}),
  },
  // Unread cards get a touch more presence: indigo-tinted border, a faint
  // tinted wash, and extra leading padding to clear the accent rail.
  listItemUnread: {
    borderColor: Colors.accentLine,
    backgroundColor: Colors.accentSoft,
    paddingLeft: 26,
  },
  // Hover: lift the card and deepen the shadow + border.
  listItemHover: {
    borderColor: Colors.accentLine,
    ...(Shadow.lg as object),
    ...(Platform.OS === 'web' ? { transform: 'translateY(-2px)' } as any : {}),
  },
  // Vertical accent rail pinned to the leading edge of unread cards — a soft
  // indigo gradient for a premium, dimensional accent.
  unreadRail: {
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
    backgroundColor: Colors.accent,
    ...(Platform.OS === 'web' ? { backgroundImage: 'linear-gradient(180deg,#6366F1,#4F46E5)' } as any : {}),
  },

  unreadDot: {
    width: 8, height: 8, borderRadius: Radii.full, marginTop: 8, flexShrink: 0,
    backgroundColor: Colors.transparent,
  },
  unreadDotActive: { backgroundColor: Colors.accent },

  channelIconWrap: {
    width: 18, height: 18, marginTop: 5, flexShrink: 0,
    borderRadius: 4, backgroundColor: Colors.paper3,
    alignItems: 'center', justifyContent: 'center',
  },

  itemBody: { flex: 1, minWidth: 0 },
  itemRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 5 },
  itemRowGap: { marginBottom: 6 },

  senderName: { fontSize: 13.5, fontWeight: FontWeight.medium, color: Colors.ink, flexShrink: 0 },
  senderNameUnread: { fontWeight: FontWeight.semibold },
  senderEmail: { fontSize: 11.5, color: Colors.ink4, flexShrink: 1, minWidth: 0 },
  mentionTag: { fontSize: 11, color: Colors.info, fontWeight: FontWeight.semibold },
  bounceWrap: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  bounceText: { fontSize: 11, color: Colors.warn, fontWeight: FontWeight.medium },

  // Premium ticket-number badge — a soft indigo-tinted pill with a hairline
  // border and crisp monospace numerals. Light, refined, never heavy black.
  ticketIdBadge: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: Colors.card, borderRadius: Radii.r2,
    borderWidth: 1, borderColor: Colors.accentLine,
    paddingHorizontal: 7, paddingVertical: 2,
  },
  ticketIdHash: {
    fontSize: 10.5, fontWeight: '700', color: Colors.accent,
    opacity: 0.5, marginRight: 1,
    ...(Platform.OS === 'web' ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as any : {}),
  },
  ticketIdText: {
    fontSize: 11, fontWeight: '700', color: Colors.accentInk, letterSpacing: 0.4,
    ...(Platform.OS === 'web' ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } as any : {}),
  },
  assignedPill: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: Colors.accentSoft,
    borderRadius: Radii.full, paddingHorizontal: 7, paddingVertical: 2,
  },
  assignedPillText: { fontSize: 10.5, fontWeight: FontWeight.semibold, color: Colors.accent },
  assignedOther: { fontSize: 11, color: Colors.ink4 },
  ticketSubject: { fontSize: 13, color: Colors.ink, flex: 1, fontWeight: FontWeight.regular },
  ticketSubjectUnread: { fontWeight: FontWeight.medium },

  snippet: { fontSize: 12, color: Colors.ink3, lineHeight: 18, marginTop: 2 },

  // High-level facts footer — status · created date · category · assignee.
  // Quiet, evenly-spaced metadata that gives each card real depth at a glance.
  metaFooter: {
    flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap',
    gap: 10, marginTop: 12,
    paddingTop: 11, borderTopWidth: 1, borderTopColor: Colors.line,
  },
  metaPill: { flexDirection: 'row', alignItems: 'center', gap: 5, minWidth: 0 },
  metaPillText: { fontSize: 11.5, color: Colors.ink3, fontWeight: FontWeight.medium },
  metaPillTextStrong: { color: Colors.ink2, fontWeight: FontWeight.semibold },
  metaDivider: { width: 1, height: 12, backgroundColor: Colors.line },
  statusDot: { width: 7, height: 7, borderRadius: Radii.full },

  itemMeta: { flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 },
  metaTime: { fontSize: 11.5, color: Colors.ink4 },
  msgCount: { fontSize: 10.5, color: Colors.ink4 },

  // Delete action — subtle by default, firms up (and reddens) on row hover.
  deleteBtn: {
    width: 30, height: 30, borderRadius: Radii.r2,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    marginTop: -2, opacity: 0.6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  deleteBtnVisible: { opacity: 1, backgroundColor: Colors.dangerSoft },

  // Empty state
  empty: { paddingVertical: 60, alignItems: 'center', gap: 8 },
  emptyTitle: { fontSize: 14, fontWeight: FontWeight.medium, color: Colors.ink2, marginTop: 4 },
  emptySub: { fontSize: 12.5, color: Colors.ink3 },
});
